import axios from 'axios';
import { DEVOPS_CONFIG, isDevOpsConfigComplete, getConfigMissingMessage } from '../config/devOpsConfig';

// Interfaces para Azure DevOps
export interface DevOpsConfig {
  organization: string;
  project: string;
  teams: string[]; // Array de equipos
  personalAccessToken: string;
}

export interface TeamMember {
  id: string;
  displayName: string;
  uniqueName?: string;
  imageUrl?: string;
}

export interface Activity {
  capacityPerDay: number;
  name: string;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface TeamMemberCapacity {
  teamMember: TeamMember;
  activities: Activity[];
  daysOff?: DateRange[];
  teamName?: string; // Agregamos el nombre del equipo
}

export interface CapacityResponse {
  count: number;
  value: TeamMemberCapacity[];
}

export interface Iteration {
  id: string;
  name: string;
  path: string;
  attributes: {
    startDate: string;
    finishDate: string;
    timeFrame: 'past' | 'current' | 'future';
  };
}

export interface WorkItem {
  id: number;
  fields: {
    'System.Title': string;
    'System.State': string;
    'System.AssignedTo'?: {
      displayName: string;
      uniqueName: string;
    };
    'Microsoft.VSTS.Scheduling.RemainingWork'?: number;
    'Microsoft.VSTS.Scheduling.CompletedWork'?: number;
    'Microsoft.VSTS.Scheduling.OriginalEstimate'?: number;
  };
}

class AzureDevOpsService {
  private config: DevOpsConfig;

  constructor() {
    // Usar configuración centralizada
    this.config = DEVOPS_CONFIG;
  }

  private getBaseUrlForTeam(teamName: string) {
    return `https://dev.azure.com/${this.config.organization}/${this.config.project}/${teamName}/_apis`;
  }

  private getHeaders() {
    
    const token = btoa(`:${this.config.personalAccessToken}`);
    return {
      'Authorization': `Basic ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // Obtener iteraciones de un equipo específico
  async getTeamIterations(teamName: string): Promise<Iteration[]> {

    try {
      const baseUrl = this.getBaseUrlForTeam(teamName);
      const response = await axios.get(
        `${baseUrl}/work/teamsettings/iterations?api-version=6.0`,
        { headers: this.getHeaders() }
      );
      return response.data.value;
    } catch (error) {
      console.error(`Error fetching iterations for team ${teamName}:`, error);
      throw error;
    }
  }

  // Obtener la iteración actual de un equipo específico
  async getCurrentIteration(teamName: string): Promise<Iteration | null> {
    const iterations = await this.getTeamIterations(teamName);
    return iterations.find(iter => iter.attributes.timeFrame === 'current') || null;
  }

  // Obtener capacidad del equipo para una iteración
  async getTeamCapacity(teamName: string, iterationId: string): Promise<CapacityResponse> {

    try {
      const baseUrl = this.getBaseUrlForTeam(teamName);
      const response = await axios.get(
        `${baseUrl}/work/teamsettings/iterations/${iterationId}/capacities?api-version=6.0`,
        { headers: this.getHeaders() }
      );
      
      // Agregar el nombre del equipo a cada capacidad
      const result = response.data;
      result.value = result.value.map((capacity: TeamMemberCapacity) => ({
        ...capacity,
        teamName: teamName
      }));
      
      return result;
    } catch (error) {
      console.error(`Error fetching team capacity for ${teamName}:`, error);
      throw error;
    }
  }

  // Obtener capacidad de un miembro específico
  async getMemberCapacity(teamName: string, iterationId: string, teamMemberId: string): Promise<TeamMemberCapacity | null> {

    try {
      const baseUrl = this.getBaseUrlForTeam(teamName);
      const response = await axios.get(
        `${baseUrl}/work/teamsettings/iterations/${iterationId}/capacities/${teamMemberId}?api-version=6.0`,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching member capacity:', error);
      return null;
    }
  }

  // Buscar capacidad por email en todos los equipos configurados
  async findMemberCapacityByEmail(email: string): Promise<TeamMemberCapacity | null> {

    try {
      // Buscar en todos los equipos configurados
      for (const teamName of this.config.teams) {
        try {
          const currentIteration = await this.getCurrentIteration(teamName);
          if (!currentIteration) continue;

          const teamCapacity = await this.getTeamCapacity(teamName, currentIteration.id);
          
          // Buscar por email o nombre
          const member = teamCapacity.value.find(capacity => 
            capacity.teamMember.uniqueName?.toLowerCase().includes(email.toLowerCase()) ||
            capacity.teamMember.displayName.toLowerCase().includes(email.split('@')[0].toLowerCase())
          );

          if (member) {
            return member; // Encontrado en este equipo
          }
        } catch (teamError) {
          console.warn(`Error searching in team ${teamName}:`, teamError);
          continue; // Continuar con el siguiente equipo
        }
      }

      return null; // No encontrado en ningún equipo
    } catch (error) {
      console.error('Error finding member capacity by email:', error);
      return null;
    }
  }

  // Obtener work items asignados a un usuario
  async getUserWorkItems(userEmail: string, iterationPath?: string): Promise<WorkItem[]> {

    try {
      let wiql = `SELECT [System.Id], [System.Title], [System.State], [System.AssignedTo], [Microsoft.VSTS.Scheduling.RemainingWork], [Microsoft.VSTS.Scheduling.CompletedWork] 
                  FROM WorkItems 
                  WHERE [System.AssignedTo] = '${userEmail}'`;
      
      if (iterationPath) {
        wiql += ` AND [System.IterationPath] = '${iterationPath}'`;
      }

      wiql += ` AND [System.State] <> 'Closed' AND [System.State] <> 'Removed'`;

      const response = await axios.post(
        `https://dev.azure.com/${this.config.organization}/${this.config.project}/_apis/wit/wiql?api-version=6.0`,
        { query: wiql },
        { headers: this.getHeaders() }
      );

      if (response.data.workItems && response.data.workItems.length > 0) {
        const workItemIds = response.data.workItems.map((wi: any) => wi.id);
        const detailsResponse = await axios.get(
          `https://dev.azure.com/${this.config.organization}/${this.config.project}/_apis/wit/workitems?ids=${workItemIds.join(',')}&api-version=6.0`,
          { headers: this.getHeaders() }
        );
        return detailsResponse.data.value;
      }

      return [];
    } catch (error) {
      console.error('Error fetching user work items:', error);
      return [];
    }
  }

  // Calcular días laborables entre dos fechas (excluyendo fines de semana)
  private calculateWorkingDays(startDate: Date, endDate: Date): number {
    // Validación inicial
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.error('Invalid dates in calculateWorkingDays');
      return 0;
    }

    let workingDays = 0;
    const MAX_ITERATIONS = 365; // Protección: máximo 1 año
    let iterations = 0;
    const currentDate = new Date(startDate.getTime()); // Crear copia para no modificar original

    // Normalizar las fechas para comparar solo días (sin horas)
    const startDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    const iteratorDate = new Date(startDay.getTime());

    while (iteratorDate <= endDay && iterations < MAX_ITERATIONS) {
      const dayOfWeek = iteratorDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // No es domingo (0) ni sábado (6)
        workingDays++;
      }
      iteratorDate.setDate(iteratorDate.getDate() + 1);
      iterations++;
    }

    if (iterations >= MAX_ITERATIONS) {
      console.warn(`calculateWorkingDays hit max iterations for ${startDate} to ${endDate}`);
    }

    console.log(`📅 calculateWorkingDays: ${startDay.toDateString()} a ${endDay.toDateString()} = ${workingDays} días laborables`);
    return workingDays;
  }

  // Calcular días laborables restantes desde hoy hasta el final de la iteración
  private calculateRemainingWorkingDays(iterationEndDate: string): number {
    const today = new Date();
    const endDate = new Date(iterationEndDate);
    
    // Normalizar fechas para comparar solo días
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endDateNormalized = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    
    // Si la iteración ya terminó, devolver 0
    if (endDateNormalized < todayNormalized) {
      console.log(`📅 Iteración terminada: ${endDateNormalized.toDateString()} < ${todayNormalized.toDateString()}`);
      return 0;
    }
    
    // Calcular días laborables restantes desde hoy (inclusive)
    const remainingDays = this.calculateWorkingDays(todayNormalized, endDateNormalized);
    console.log(`📅 Días restantes: ${remainingDays} desde ${todayNormalized.toDateString()}`);
    return remainingDays;
  }

  // Calcular métricas de capacidad con días restantes reales
  calculateCapacityMetrics(capacity: TeamMemberCapacity, workItems: WorkItem[], iteration?: Iteration | null) {
    const totalCapacityPerDay = capacity.activities.reduce((sum, activity) => sum + activity.capacityPerDay, 0);
    
    const remainingWork = workItems.reduce((sum, item) => {
      return sum + (item.fields['Microsoft.VSTS.Scheduling.RemainingWork'] || 0);
    }, 0);

    const completedWork = workItems.reduce((sum, item) => {
      return sum + (item.fields['Microsoft.VSTS.Scheduling.CompletedWork'] || 0);
    }, 0);

    const totalWork = remainingWork + completedWork;
    
    // Calcular días del sprint basado en la iteración real
    let sprintWorkDays = 5; // Default fallback
    let remainingWorkDays = 5; // Default fallback
    
    if (iteration) {
      let startDate = new Date(iteration.attributes.startDate);
      let endDate = new Date(iteration.attributes.finishDate);
      
      console.log(`📋 Iteración original: ${iteration.name}, Start: ${startDate.toDateString()}, End: ${endDate.toDateString()}`);
      
      // Normalizar sprint a lunes-viernes (5 días laborables estándar)
      // Si empieza en domingo, mover al lunes
      if (startDate.getDay() === 0) { // Domingo
        startDate.setDate(startDate.getDate() + 1); // Mover al lunes
        console.log(`🔧 Fecha de inicio ajustada de domingo a lunes: ${startDate.toDateString()}`);
      }
      
      // Si empieza en sábado, mover al lunes siguiente
      if (startDate.getDay() === 6) { // Sábado
        startDate.setDate(startDate.getDate() + 2); // Mover al lunes
        console.log(`🔧 Fecha de inicio ajustada de sábado a lunes: ${startDate.toDateString()}`);
      }
      
      // Calcular qué sería un viernes a partir del lunes de inicio
      const expectedEndDate = new Date(startDate.getTime());
      let workDaysAdded = 0;
      const MAX_ITERATIONS_WORKDAYS = 30; // Protección: máximo 30 días calendario
      let iterations = 0;

      while (workDaysAdded < 4 && iterations < MAX_ITERATIONS_WORKDAYS) { // Necesitamos 4 días más después del lunes para llegar al viernes
        expectedEndDate.setDate(expectedEndDate.getDate() + 1);
        const dayOfWeek = expectedEndDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // No es fin de semana
          workDaysAdded++;
        }
        iterations++;
      }

      if (iterations >= MAX_ITERATIONS_WORKDAYS) {
        console.warn(`While loop hit max iterations while calculating expectedEndDate`);
      }
      
      // Si la fecha de fin original es diferente al viernes esperado, usar el viernes
      if (endDate < expectedEndDate || endDate.getDay() !== 5) { // No termina en viernes
        endDate = expectedEndDate;
        console.log(`🔧 Fecha de fin ajustada para terminar en viernes: ${endDate.toDateString()}`);
      }
      
      // Verificar que efectivamente tenemos 5 días laborables
      sprintWorkDays = this.calculateWorkingDays(startDate, endDate);
      console.log(`📊 Verificación: sprint normalizado tiene ${sprintWorkDays} días laborables`);
      
      // Si aún no son 5 días, forzar el ajuste
      if (sprintWorkDays !== 5) {
        // Recalcular desde el lunes hasta tener exactamente 5 días
        const correctedEndDate = new Date(startDate.getTime());
        let workDays = 1; // El lunes cuenta como día 1
        const MAX_ITERATIONS_CORRECTION = 30; // Protección: máximo 30 días calendario
        let iterations = 0;

        while (workDays < 5 && iterations < MAX_ITERATIONS_CORRECTION) {
          correctedEndDate.setDate(correctedEndDate.getDate() + 1);
          const dayOfWeek = correctedEndDate.getDay();
          if (dayOfWeek !== 0 && dayOfWeek !== 6) { // No es fin de semana
            workDays++;
          }
          iterations++;
        }

        if (iterations >= MAX_ITERATIONS_CORRECTION) {
          console.warn(`While loop hit max iterations while correcting sprint work days`);
        }

        endDate = correctedEndDate;
        sprintWorkDays = 5;
        console.log(`🔧 Sprint forzado a 5 días: ${startDate.toDateString()} a ${endDate.toDateString()}`);
      }
      
      // Calcular días restantes con las fechas normalizadas
      remainingWorkDays = this.calculateRemainingWorkingDays(endDate.toISOString());
      
      console.log(`📊 Sprint final normalizado: ${sprintWorkDays} días totales, ${remainingWorkDays} días restantes`);
      console.log(`📅 Sprint va de ${startDate.toDateString()} (${['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'][startDate.getDay()]}) a ${endDate.toDateString()} (${['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'][endDate.getDay()]})`);
    }
    
    // Calcular días libres que afectan el período restante
    const daysOffCount = capacity.daysOff?.length || 0;
    const availableWorkDays = Math.max(remainingWorkDays - daysOffCount, 0);
    
    // Capacity total original del sprint completo
    const totalSprintCapacity = totalCapacityPerDay * sprintWorkDays;
    
    // Capacity restante basada en días que quedan
    const remainingSprintCapacity = totalCapacityPerDay * availableWorkDays;
    
    // Porcentaje de utilización basado en trabajo total vs capacity total del sprint
    const utilizationPercent = totalSprintCapacity > 0 ? (totalWork / totalSprintCapacity) * 100 : 0;
    
    // Porcentaje de utilización del tiempo restante (solo trabajo pendiente)
    const remainingUtilizationPercent = remainingSprintCapacity > 0 ? (remainingWork / remainingSprintCapacity) * 100 : 0;

    return {
      totalCapacityPerDay,
      remainingWork,
      completedWork,
      totalWork,
      utilizationPercent: Math.min(utilizationPercent, 100),
      remainingUtilizationPercent: Math.min(remainingUtilizationPercent, 100),
      workItemsCount: workItems.length,
      activities: capacity.activities,
      daysOff: capacity.daysOff || [],
      sprintWorkDays,
      availableWorkDays,
      remainingWorkDays,
      totalSprintCapacity,
      remainingSprintCapacity
    };
  }
}

// Cache global y cola para evitar requests simultáneos
const devOpsCache = new Map<string, any>();
const pendingRequests = new Map<string, Promise<any>>();
const usersWithoutData = new Set<string>(); // Lista de usuarios sin datos para evitar reintentos
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Hook para usar Azure DevOps
export const useAzureDevOps = () => {
  const service = new AzureDevOpsService();

  const isConfigured = () => {
    return isDevOpsConfigComplete();
  };

  const getConfigStatus = () => {
    if (!isDevOpsConfigComplete()) {
      return {
        configured: false,
        message: getConfigMissingMessage()
      };
    }
    return {
      configured: true,
      message: `Conectado a ${DEVOPS_CONFIG.organization}/${DEVOPS_CONFIG.project} con ${DEVOPS_CONFIG.teams.length} equipos`
    };
  };

  const getUserCapacityData = async (email: string) => {
    if (!isConfigured()) {
      throw new Error(getConfigMissingMessage());
    }

    const cacheKey = `capacity_${email}`;
    
    // Si sabemos que este usuario no tiene datos, no hacer request
    if (usersWithoutData.has(email)) {
      console.log(`🚫 DevOps usuario sin datos conocido: ${email}`);
      return null;
    }
    
    // Verificar cache primero
    const cached = devOpsCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
      console.log(`📋 DevOps cache hit para ${email}`);
      return cached.data;
    }

    // Si ya hay un request en progreso, esperarlo
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest) {
      console.log(`⏳ DevOps esperando request en progreso para ${email}`);
      return await pendingRequest;
    }

    // Crear nuevo request con delay aleatorio para evitar saturación
    const delay = Math.random() * 2000; // 0-2 segundos de delay aleatorio
    const requestPromise = new Promise(async (resolve) => {
      try {
        await new Promise(resolve => setTimeout(resolve, delay));
        console.log(`🔄 DevOps iniciando request para ${email}`);

        const capacity = await service.findMemberCapacityByEmail(email);
        if (!capacity) {
          console.log(`📝 DevOps marcando usuario sin datos: ${email}`);
          usersWithoutData.add(email); // Marcar como usuario sin datos
          resolve(null);
          return;
        }

        // Usar el equipo donde se encontró al usuario
        const teamName = capacity.teamName;
        if (!teamName) {
          console.error('Team name not found in capacity data');
          resolve(null);
          return;
        }

        const currentIteration = await service.getCurrentIteration(teamName);
        const workItems = await service.getUserWorkItems(email, currentIteration?.path);
        
        const result = {
          capacity,
          workItems,
          metrics: service.calculateCapacityMetrics(capacity, workItems, currentIteration),
          iteration: currentIteration,
          teamName
        };

        // Guardar en cache
        devOpsCache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });

        console.log(`✅ DevOps datos cacheados para ${email}`);
        resolve(result);
      } catch (error) {
        console.error('Error getting user capacity data:', error);
        resolve(null); // Resolver con null en lugar de reject para evitar errores en UI
      }
    });

    // Guardar request en progreso
    pendingRequests.set(cacheKey, requestPromise);

    // Limpiar request cuando termine
    requestPromise.finally(() => {
      pendingRequests.delete(cacheKey);
    });

    return await requestPromise;
  };

  const clearCache = () => {
    devOpsCache.clear();
    pendingRequests.clear();
    usersWithoutData.clear();
    console.log('🧹 DevOps cache y lista de usuarios sin datos limpiada');
  };

  const refreshUserData = async (email: string) => {
    const cacheKey = `capacity_${email}`;
    devOpsCache.delete(cacheKey);
    pendingRequests.delete(cacheKey);
    usersWithoutData.delete(email); // Permitir reintentar este usuario
    console.log(`🔄 DevOps cache limpiado para ${email}, permitiendo reintento`);
    return getUserCapacityData(email);
  };

  return {
    isConfigured,
    getConfigStatus,
    getUserCapacityData,
    clearCache,
    refreshUserData,
    service
  };
};


export default AzureDevOpsService;